import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { rootUrl } from '../../../../config/config';
import { useAuth } from '../../AuthContext';

const formatDate = (iso) => {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        if (isNaN(d)) return iso;
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) {
        return iso;
    }
};

const formatCurrency = (v, fallback = '—') => {
    if (v === null || v === undefined || v === '') return fallback;
    const n = Number(String(v).replace(/,/g, ''));
    if (Number.isNaN(n)) return fallback;
    return `KES ${n.toLocaleString()}`;
};

const calculateDuration = (start, end) => {
    if (!start) return '—';
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : new Date();

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return '—';

    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 30) {
        return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    }

    const months = Math.floor(diffDays / 30);
    const days = diffDays % 30;

    let result = `${months} month${months !== 1 ? 's' : ''}`;
    if (days > 0) {
        result += ` ${days} day${days !== 1 ? 's' : ''}`;
    }
    return result;
};

export default function DriverAllocationsList({ driverId }) {
    const [allocations, setAllocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    useAuth();
    const token = localStorage.getItem('authToken');

    useEffect(() => {
        if (!driverId) return;

        const fetchAllocations = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await fetch(`${rootUrl}/cars/drivers/driver/${driverId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    if (response.status === 404) {
                        setAllocations([]);
                        return;
                    }
                    throw new Error('Failed to fetch allocations');
                }

                const data = await response.json();
                setAllocations(data.data || []);
            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchAllocations();
    }, [driverId, token]);

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF5733]"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-8 text-red-600 bg-red-50 rounded-lg">
                <p>Error: {error}</p>
            </div>
        );
    }

    if (allocations.length === 0) {
        return (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                <p className="text-gray-500">No vehicle assignments found for this driver.</p>
            </div>
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
                <thead className="bg-gray-50 text-gray-600 font-medium border-b border-gray-200">
                    <tr>
                        <th className="py-3 px-4">Owner</th>
                        <th className="py-3 px-4">Plate No</th>
                        <th className="py-3 px-4">Start Date</th>
                        <th className="py-3 px-4">End Date</th>
                        <th className="py-3 px-4">Duration</th>
                        <th className="py-3 px-4">Status</th>
                        <th className="py-3 px-4 text-right">Car WAR</th>
                        <th className="py-3 px-4 text-right">Driver WAR</th>
                        <th className="py-3 px-4 text-right">Weekly Amount</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {allocations.map((allocation) => {
                        const isActive = !allocation.end_date;
                        return (
                            <tr key={allocation.allocation_number || allocation.id} className="hover:bg-gray-50 transition-colors">
                                <td className="py-3 px-4">
                                    <div className="font-medium text-gray-900">
                                        <Link
                                            to={`/owners/${allocation.owner_code || allocation.owner}`}
                                            className="text-[#FF5733] hover:underline hover:text-[#E64A19] transition-colors"
                                        >
                                            {allocation.owner_name || allocation.owner || allocation.owner_code || 'Owner'}
                                        </Link>
                                    </div>
                                    <div className="text-xs text-gray-500">{allocation.owner_code || ''}</div>
                                </td>
                                <td className="py-3 px-4 font-mono text-gray-700">
                                    <Link
                                        to={`/cars/${allocation.plate_no}`}
                                        className="text-[#FF5733] hover:underline hover:text-[#E64A19] transition-colors"
                                    >
                                        {allocation.plate_no}
                                    </Link>
                                </td>
                                <td className="py-3 px-4 text-gray-700">
                                    {formatDate(allocation.start_date)}
                                </td>
                                <td className="py-3 px-4 text-gray-700">
                                    {allocation.end_date ? formatDate(allocation.end_date) : <span className="text-green-600 italic">Current</span>}
                                </td>
                                <td className="py-3 px-4 text-gray-700">
                                    {calculateDuration(allocation.start_date, allocation.end_date)}
                                </td>
                                <td className="py-3 px-4">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isActive
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-gray-100 text-gray-800'
                                        }`}>
                                        {isActive ? 'Active' : 'Ended'}
                                    </span>
                                </td>
                                <td className="py-3 px-4 text-gray-700 text-right font-mono">
                                    {formatCurrency(allocation.car_war_sum)}
                                </td>
                                <td className="py-3 px-4 text-gray-700 text-right font-mono">
                                    {formatCurrency(allocation.driver_war_sum)}
                                </td>
                                <td className="py-3 px-4 text-gray-700 text-right font-mono">
                                    {allocation.weekly_amount ? `KES ${Number(allocation.weekly_amount).toLocaleString()}` : '—'}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
